/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: Lobby.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  05.07.22 9:40 am.
 * Last modified: 05.07.22 9:40 am.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect, useRef } from 'react';
import { debounceTime, fromEvent } from 'rxjs';
import { getSectionScrollPossition } from './api';
import { getBannerOrientation, getGameJackpotComponents, getSectionJackpotComponent } from './api/helpers';
import { defaultTranslations, translations } from './api/translations';
import SectionContainer from './components/SectionContainer';
import { LOBBY_SECTION_NAME, SEARCH_SECTION_NAME } from './constants';
import { useSectionContainerRef } from './contexts/SectionContainerRefContext';
import { useSelectedSection, useSettings } from './contexts/SettingsContext';
import eventStore from './eventStore';
import FavoritesSection from './FavoritesSection';
import Game from './Game';
import NavigationContainer from './NavigationBar/NavigationContainer';
import StatusContainer from './NavigationBar/StatusContainer';
import SearchSection from './SearchSection';
import SearchBar from './SearchSection/SearchBar';
import Section from './Section';
import MostPlayedSection from './Sections/MostPlayedSection';
import SettingsModal from './SettingsModal';
import { soundManager } from './soundManager';
import InGameHeader from './components/InGameHeader';

const Lobby = () => {
    const [settings, setSettings] = useSettings();

    const sectionContainerRef = useRef<HTMLDivElement>(null);
    const [, setSectionContainerRef] = useSectionContainerRef();
    useEffect(
        () => () => {
            soundManager?.play('silent');
        },
        [],
    );

    useEffect(() => {
        defaultTranslations();
        const subscription = eventStore.SETTINGS.subscribe(value => {
            const currentTranslations = eventStore.TRANSLATIONS.value;
            if (
                (!currentTranslations.hasOwnProperty('translationCode') ||
                    currentTranslations.translationCode !== value.languageCode) &&
                value.languageCode &&
                value.languageCode.length
            ) {
                translations(value.languageCode);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const [selectedSection, setSelectedSection] = useSelectedSection();

    useEffect(() => {
        if (sectionContainerRef.current) {
            setSectionContainerRef(sectionContainerRef as React.MutableRefObject<HTMLElement>);
            const scrollPossition = getSectionScrollPossition(selectedSection);
            const animationFrameScrollSetter = () => {
                if (
                    scrollPossition.scrollHeight &&
                    sectionContainerRef.current &&
                    scrollPossition.scrollHeight > sectionContainerRef.current.scrollHeight
                ) {
                    requestAnimationFrame(animationFrameScrollSetter);
                } else if (sectionContainerRef.current) {
                    sectionContainerRef.current.scrollTop = scrollPossition.top;
                }
            };
            requestAnimationFrame(animationFrameScrollSetter);
            if (sectionContainerRef.current) {
                sectionContainerRef.current.scrollTop = scrollPossition.top;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSection, sectionContainerRef, setSectionContainerRef]);

    useEffect(() => {
        if (sectionContainerRef.current) {
            const subscription = fromEvent(sectionContainerRef.current, 'scroll')
                .pipe(debounceTime(400))
                .subscribe(e => {
                    // @ts-ignore
                    const topScroll = e.target.scrollTop;
                    // @ts-ignore
                    const scrollHeight = e.target.scrollHeight;
                    eventStore.SCROLL_SECTION.next({
                        name: eventStore.SETTINGS.value.selectedSection,
                        type: 'top',
                        value: topScroll,
                        height: scrollHeight,
                    });
                });
            return () => subscription.unsubscribe();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionContainerRef]);

    const isDisplayingAll = selectedSection === LOBBY_SECTION_NAME;

    const handleClickIcon = isDisplayingAll
        ? undefined
        : () => {
              setSelectedSection(LOBBY_SECTION_NAME);
          };

    const handleClickPlay = ({
        gameKey,
        banners,
        sectionName,
        sectionPosition,
    }: {
        gameKey: string;
        banners: { rows: number; cols: number };
        sectionName: string;
        sectionPosition: number;
    }) => () => {
        eventStore.ON_ANALYTICS_EVENT.next({
            gameKey,
            banners,
            sectionName,
            sectionPosition,
        });
        eventStore.START_GAME.next(gameKey);
    };

    return (
        <>
            <div
                className={`
                egt_lobby
                egt_lobby_${settings.mode}
                ${
                    settings.mode === 'mobile' && (settings.isSettingsModalOpen || settings.isSearchOpen)
                        ? 'egt_lobby_zero_navigator_height'
                        : ''
                }
            `}
            >
                <InGameHeader />
                <SectionContainer ref={sectionContainerRef}>
                    {settings.isSearchOpen && settings.mode === 'mobile' && <SearchBar variant="mobile" />}
                    {settings.sections
                        .filter(sectionSetting => {
                            if (settings.isSearchOpen) {
                                return sectionSetting.name === SEARCH_SECTION_NAME;
                            } else {
                                return true;
                            }
                        })
                        .filter(sectionSetting => sectionSetting.games.length)
                        .map((sectionSetting, sectionPosition) => {
                            let SectionComponent;
                            if (sectionSetting.name.indexOf('MOST_PLAYED') !== -1) {
                                SectionComponent = MostPlayedSection;
                            } else {
                                SectionComponent = Section;
                            }

                            return (
                                <SectionComponent
                                    group={sectionSetting.name}
                                    count={sectionSetting.games.length}
                                    blocks={sectionSetting.gridBlocks}
                                    label={sectionSetting.name}
                                    {...getSectionJackpotComponent(sectionSetting.jackpotName, sectionSetting.name)}
                                    key={sectionSetting.name}
                                    isViewingAll={!isDisplayingAll}
                                    onClickViewAll={() => setSelectedSection(sectionSetting.name)}
                                    onClickIcon={handleClickIcon}
                                    mode={settings.mode}
                                    visible={
                                        isDisplayingAll || (!isDisplayingAll && selectedSection === sectionSetting.name)
                                    }
                                >
                                    {sectionSetting.games.map((game, i) => {
                                        const banners = isDisplayingAll ? game.banners.lobby : game.banners.section;
                                        const {
                                            jackpotComponent: GameJackpotComponent,
                                            jackpotComponentRenderInside,
                                        } = getGameJackpotComponents(game.gameKey);
                                        const isMostPlayed =
                                            sectionSetting.name.toLowerCase().indexOf('most_played') !== -1;

                                        return (
                                            <Game
                                                gameKey={game.gameKey}
                                                name={game.name}
                                                cols={banners.cols}
                                                rows={banners.rows}
                                                key={`${sectionSetting.name}-${game.gameKey}`}
                                                jackpotName={game.jackpotName || undefined}
                                                jackpotCurrency={settings.currency}
                                                minDenomination={game.minDenomination.get(settings.currency)}
                                                minBet={game.minPossibleBets.get(settings.currency) || 0}
                                                maxBet={game.maxPossibleBets.get(settings.currency) || 0}
                                                branding={game.branding}
                                                onClickPlay={handleClickPlay({
                                                    gameKey: game.gameKey,
                                                    banners,
                                                    sectionName: sectionSetting.name,
                                                    sectionPosition: sectionPosition + 1,
                                                })}
                                                jackpotComponent={
                                                    GameJackpotComponent &&
                                                    (() => (
                                                        <GameJackpotComponent
                                                            gameKey={game.gameKey}
                                                            groupName={sectionSetting.name}
                                                            bannerType={getBannerOrientation({
                                                                rows: banners.rows,
                                                                cols: banners.cols,
                                                            })}
                                                            jackpotName={game.jackpotName || undefined}
                                                            denomination={game.minDenomination.get(settings.currency)}
                                                            currency={settings.currency}
                                                            units={settings.units}
                                                            maxBet={game.maxBet?.get(settings.currency)}
                                                        />
                                                    ))
                                                }
                                                jackpotComponentRenderInside={jackpotComponentRenderInside}
                                                mostPlayedIndex={isDisplayingAll && isMostPlayed ? `${game.order}` : ''}
                                            />
                                        );
                                    })}
                                    {sectionSetting.games.length === 1}
                                </SectionComponent>
                            );
                        })}
                    {!settings.isSearchOpen && <FavoritesSection />}
                    <SearchSection />
                </SectionContainer>
                {(!settings.isSearchOpen || settings.mode === 'mobile') && (
                    <NavigationContainer
                        onClickSearch={() =>
                            setSettings?.({
                                ...settings,
                                isSearchOpen: !settings.isSearchOpen,
                                isSettingsModalOpen: false,
                            })
                        }
                        onClickSettings={() => {
                            setSettings?.({
                                ...settings,
                                isSettingsModalOpen: !settings.isSettingsModalOpen,
                                isSearchOpen: false,
                            });
                        }}
                    />
                )}
                {settings.isSearchOpen && settings.mode === 'desktop' && <SearchBar variant="desktop" />}
                <StatusContainer
                    onClickSearch={() =>
                        setSettings?.({
                            ...settings,
                            isSearchOpen: !settings.isSearchOpen,
                            isSettingsModalOpen: false,
                        })
                    }
                    onClickSettings={() =>
                        setSettings?.({
                            ...settings,
                            isSettingsModalOpen: !settings.isSettingsModalOpen,
                            isSearchOpen: false,
                        })
                    }
                />
                {settings.isSettingsModalOpen && <SettingsModal />}
            </div>
        </>
    );
};

export default Lobby;
